import { useCallback, useEffect, useState } from "react";
import { UserPlus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TempPasswordPanel } from "@/components/auth/TempPasswordPanel";

const sel = "h-9 rounded-md border border-slate-300 bg-white text-sm px-2";

const AddLogin = ({ cid, roles, onDone }) => {
  const [f, setF] = useState({ email: "", name: "", role: "member" });
  const go = async (e) => {
    e.preventDefault();
    try { const r = await api.post(`/committees/${cid}/members`, f); toast.success("Access granted"); onDone(r.data.temp_password ? { email: f.email.trim().toLowerCase(), password: r.data.temp_password } : null); setF({ email: "", name: "", role: "member" }); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <form onSubmit={go} className="flex flex-col md:flex-row gap-2 p-3 rounded-md border border-slate-200 bg-slate-50/60" data-testid="add-member-form">
      <Input placeholder="Email address" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-white" data-testid="member-email-input" />
      <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-white" data-testid="member-name-input" />
      <select className={sel} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="member-role-select">
        {Object.entries(roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <Button type="submit" disabled={!f.email.includes("@")} data-testid="member-add-button"><UserPlus className="w-4 h-4 mr-1.5" />Add</Button>
    </form>
  );
};

export const AccessPanel = ({ open, onClose, c, me, roles }) => {
  const [list, setList] = useState([]);
  const [temp, setTemp] = useState(null);
  const load = useCallback(() => api.get(`/committees/${c.committee_id}/members`).then((r) => setList(r.data)), [c.committee_id]);
  useEffect(() => { if (open) { load(); setTemp(null); } }, [open, load]);
  const reset = async (m) => {
    if (!window.confirm(`Reset ${m.name}'s password? They will be signed out everywhere.`)) return;
    try { const r = await api.post(`/committees/${c.committee_id}/members/${m.user_id}/reset-password`); setTemp({ email: r.data.email, password: r.data.temp_password }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="access-panel">
        <DialogHeader>
          <DialogTitle>Access · {c.name}</DialogTitle>
          <DialogDescription>Login accounts that can open this committee page. This is separate from the member list.</DialogDescription>
        </DialogHeader>
        <AddLogin cid={c.committee_id} roles={roles} onDone={(t) => { setTemp(t); load(); }} />
        {temp && <TempPasswordPanel email={temp.email} password={temp.password} />}
        <div className="divide-y divide-slate-100" data-testid="committee-members">
          {list.length === 0 && <p className="p-4 text-sm text-slate-500">No login accounts yet. Add a Committee Admin first.</p>}
          {list.map((m) => (
            <div key={m.user_id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`member-row-${m.email}`}>
              <div className="min-w-0 flex-1"><div className="text-sm font-medium">{m.name}</div><div className="text-xs text-slate-500 truncate">{m.email} · last sign-in {m.last_login ? fmtDate(m.last_login) : "never"}</div></div>
              {m.user_id !== me ? (<>
                <select className={sel} value={m.role} onChange={(e) => act(() => api.patch(`/committees/${c.committee_id}/members/${m.user_id}`, { role: e.target.value }), "Role updated")} data-testid={`member-role-${m.email}`}>
                  {Object.entries(roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <Button size="sm" variant="outline" onClick={() => reset(m)} data-testid={`member-reset-password-${m.email}`}><KeyRound className="w-3.5 h-3.5 mr-1" />Reset password</Button>
                <Button size="sm" variant="ghost" onClick={() => window.confirm(`Remove ${m.name}'s access to ${c.name}?`) && act(() => api.delete(`/committees/${c.committee_id}/members/${m.user_id}`), "Access removed")} data-testid={`member-remove-${m.email}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
              </>) : <span className="text-xs font-semibold text-slate-600">{m.role_label}</span>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
